/**
 * CRMDetailSkeleton - Loading skeleton for the CRM record detail page.
 *
 * Mirrors the CURRENT CRMObjectFullPageView layout to prevent layout shift:
 *  - Main column: a compact top bar (avatar + name + tab strip + actions) with
 *    a full-width bottom border, the Overview content (highlights grid →
 *    relationship line → AI summary → recent activity), and a pinned chat input.
 *  - Right rail: the "Contact overview" header + a list of property rows,
 *    separated by a left border.
 */

import type { CSSProperties } from 'react';

import { SkeletonLoader } from '@/lib/components/SkeletonLoader';

import { RAIL_DEFAULT_WIDTH } from '../hooks/useRailWidth';
import { TimelineSkeleton } from '../timeline';
import { SummaryCardShell, SummaryLoadingSkeleton } from './summaryPanelShell';

/** Six tiles — matches the typical highlights grid (`grid-cols-2 md:grid-cols-3`). */
const HIGHLIGHT_TILES = [0, 1, 2, 3, 4, 5];
// Six rows ≈ a typical record's field count, so the rail barely resizes when
// the real OverviewSection rows replace the skeleton.
const PROPERTY_ROWS = [0, 1, 2, 3, 4, 5];
const TAB_LABELS = [0, 1, 2, 3, 4];

interface CRMDetailSkeletonProps {
  /**
   * Rail width in pixels, injected as a CSS variable so the skeleton's right
   * rail matches the user's persisted resize choice — otherwise the skeleton
   * renders at the 340px default and the rail visibly jumps once the real
   * (resized) rail mounts. Defaults to RAIL_DEFAULT_WIDTH so callers with no
   * persisted rail (e.g. MeetingSummaryFullPage, which has no resizable rail)
   * are pixel-identical to before this prop existed.
   */
  railWidth?: number;
}

export function CRMDetailSkeleton({
  railWidth = RAIL_DEFAULT_WIDTH,
}: CRMDetailSkeletonProps = {}) {
  return (
    <main
      className='min-h-full lg:h-full lg:overflow-hidden flex flex-col bg-[var(--color-bg-primary)]'
      role='status'
      aria-label='Loading record'
    >
      {/* Top padding lives on the columns (not this wrapper) so the rail's
          left border can run the full height to the very top — matches
          CRMObjectFullPageView so the divider doesn't shift on load. */}
      <div className='px-6 flex-1 min-h-0'>
        <div className='flex flex-col gap-6 lg:flex-row lg:gap-0 h-full'>
          {/* Main column: top bar + Overview content + chat input */}
          <section
            aria-label='Activity'
            className='flex-1 min-w-0 flex flex-col pt-4 lg:pr-6'
          >
            <div className='flex-1 min-h-0 flex flex-col'>
              {/* Top bar — mirrors FullPageTabs' bar (avatar + name + tabs +
                  actions), bled full-width with a bottom border. Overview is a
                  NON-fill tab, so the live bar uses `pb-4 border-b` (FullPageTabs
                  adds 6px back so the divider lands at the fill-tab height) — the
                  skeleton must match `pb-4`, not `pb-2.5`, or the border (and
                  everything below it) sits 6px too high. */}
              <div className='flex items-center gap-3 shrink-0 pb-4 lg:-mx-6 lg:px-4 border-b border-[var(--color-border-secondary)]'>
                {/* Leading: avatar + name, grouped like the live `leading` slot
                    (`flex items-center gap-2.5 min-w-0`) — not bare gap-3
                    siblings, so the avatar↔name gap is 10px to match. */}
                <div className='flex min-w-0 items-center gap-2.5'>
                  <SkeletonLoader
                    variant='circle'
                    width='32px'
                    height='32px'
                    className='shrink-0'
                  />
                  {/* ~96px ≈ a typical "First Last" record name, so the leading
                      group width (and therefore where the tab strip starts) is
                      close to the live header — the tabs barely shift on load.
                      (A wider 140px bar made the tabs jump ~48px left.) */}
                  <SkeletonLoader width='96px' height='18px' />
                </div>
                {/* Tab strip fills the row (mirrors OverflowBar's
                    `min-w-0 flex-1`) so the actions are pushed to the far end,
                    rather than sitting at a fixed `ml-4` offset. */}
                <div className='hidden min-w-0 flex-1 items-center gap-2 md:flex'>
                  {TAB_LABELS.map(i => (
                    <SkeletonLoader key={i} width='72px' height='20px' />
                  ))}
                </div>
                {/* Trailing actions: a labeled button + two icon buttons. The
                    labeled button is 36px tall to match the live actions group
                    (the tallest element in the real top bar) so the bar is the
                    same 53px height — without this the skeleton bar was 49px and
                    everything below shifted down ~4px on load. */}
                <div className='ml-auto flex items-center gap-1'>
                  <SkeletonLoader
                    width='96px'
                    height='36px'
                    borderRadius='0.5rem'
                  />
                  <SkeletonLoader variant='circle' width='28px' height='28px' />
                  <SkeletonLoader variant='circle' width='28px' height='28px' />
                </div>
              </div>

              {/* Overview content: highlights grid → relationship line → AI
                  summary → recent activity (matches OverviewTab order). */}
              <div className='flex-1 min-h-0 overflow-hidden pt-4 space-y-6'>
                {/* Highlights grid — mirrors HighlightsGrid + HighlightCardShell:
                    filled, borderless grey tiles (NOT outlined white boxes), an
                    11px label row with a top-right content icon, then the value
                    on a `min-h-6` row. Matches the live tiles so nothing
                    reflows or restyles when content loads. */}
                <div className='grid grid-cols-2 md:grid-cols-3 gap-3'>
                  {HIGHLIGHT_TILES.map(i => (
                    <div
                      key={i}
                      className='bg-[var(--color-bg-secondary)] rounded-xl px-3.5 py-3'
                    >
                      <div className='flex items-start justify-between gap-2 mb-1.5'>
                        <SkeletonLoader width='60%' height='11px' />
                        <SkeletonLoader
                          variant='circle'
                          width='14px'
                          height='14px'
                          className='shrink-0'
                        />
                      </div>
                      <div className='min-h-6 flex items-center'>
                        {/* 5rem matches HighlightCardShell's ValueArea loading
                            Skeleton so the value placeholder doesn't resize. */}
                        <SkeletonLoader width='5rem' height='16px' />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Relationship status line — matches RelationshipStatusLine's
                    own loading skeleton (200px × 1rem). */}
                <SkeletonLoader width='200px' height='16px' />

                {/* AI summary — reuse the real SummaryCardShell + the shared
                    SummaryLoadingSkeleton ResearchSummaryPanel shows, so this
                    section is pixel-identical to the live loading state AND
                    sized to the common empty-state height (no collapse/reflow of
                    the activity feed below when research resolves). */}
                <SummaryCardShell>
                  <SummaryLoadingSkeleton />
                </SummaryCardShell>

                {/* Recent activity preview — reuse the real timeline loading
                    skeleton (the same component TimelineTab renders while events
                    load) so the rows don't change shape on hand-off, then the
                    "View all activity" link, matching RecentActivityPreview's
                    `space-y-3` layout. */}
                <div className='space-y-3'>
                  <TimelineSkeleton
                    noBorder
                    hideHeader
                    hideFilters
                    entityType=''
                  />
                  <SkeletonLoader width='120px' height='16px' />
                </div>
              </div>
            </div>

            {/* Chat input pinned at the bottom of the main column */}
            <div className='shrink-0 border-t border-[var(--color-border-secondary)] px-6 py-4'>
              <div className='mx-auto w-full max-w-[700px]'>
                <SkeletonLoader height='44px' borderRadius='0.75rem' />
              </div>
            </div>
          </section>

          {/* Right rail: property details ("Contact overview"). border-PRIMARY on
              the left edge mirrors CRMObjectFullPageView's rail so the full-height
              divider is visible to the top and doesn't change on load. */}
          <section
            aria-label='Record details'
            className='w-full lg:w-[var(--skeleton-rail-w)] lg:shrink-0 lg:h-full lg:pl-6 lg:border-l border-[var(--color-border-primary)] flex flex-col'
            style={{ '--skeleton-rail-w': `${railWidth}px` } as CSSProperties}
          >
            <div className='flex-1 lg:overflow-y-auto pt-4 pb-6'>
              {/* Section header: title + Edit + collapse (mirrors the
                  non-collapsible CollapsibleSection header, `pb-2.5`). */}
              <div className='flex items-center gap-2 pb-2.5'>
                <SkeletonLoader
                  width='130px'
                  height='18px'
                  className='flex-1'
                />
                <SkeletonLoader width='44px' height='20px' />
                <SkeletonLoader variant='circle' width='28px' height='28px' />
              </div>

              {/* Property rows: icon + label + value. Mirrors the live row in
                  OverviewSection.renderField — a 14px property-type glyph
                  (square, not a circle), a fixed `w-32` label column, then the
                  value. The label MUST reserve `w-32` (128px) so the value
                  column lines up exactly with the live rows. */}
              <div className='space-y-3 py-1.5'>
                {PROPERTY_ROWS.map(i => (
                  <div key={i} className='flex items-start gap-3'>
                    <SkeletonLoader
                      width='14px'
                      height='14px'
                      borderRadius='0.25rem'
                      className='mt-0.5 shrink-0'
                    />
                    <div className='w-32 shrink-0'>
                      <SkeletonLoader width='70%' height='14px' />
                    </div>
                    <SkeletonLoader width='45%' height='14px' />
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
